import React, { useState } from 'react';
import { TextInput, ActionIcon, Pill } from '@mantine/core';
import type { TextInputProps } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus as faPlusDef } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import classes from './quick-notes-field.module.scss';

const faPlus = faPlusDef as IconProp;
const MAX_NOTE_LENGTH = 120;
const MAX_NOTES = 30;

export type QuickNotesFieldProps = {
  value: string[];
  disabled?: boolean;
  onChange: (next: string[]) => void;
  label?: string;
  addLabel?: string;
  inputProps?: TextInputProps;
  parseValues?: (next: string[]) => string[] | null;
};

// Summary: Chip editor for a category's kitchen-note shortcuts (הערות מהירות).
const QuickNotesFieldComponent: React.FC<QuickNotesFieldProps> = ({
  value, disabled = false, onChange, label = 'הערות מהירות', addLabel = 'הוסף הערה מהירה', inputProps, parseValues,
}) => {
  const [draft, setDraft] = useState('');
  const trimmed = draft.trim();
  const next = parseValues ? parseValues([...value, trimmed]) : [...value, trimmed];
  const canAdd = trimmed.length > 0 && value.length < MAX_NOTES && !value.includes(trimmed)
    && next !== null && next.length > value.length;

  const add = () => {
    if (!canAdd || !next) return;
    onChange(next);
    setDraft('');
  };

  return (
    <div className={classes.field}>
      <span className={classes.label}>{label}</span>
      <div className={classes.inputRow}>
        <TextInput
          data-testid="category-quick-notes-input"
          placeholder="הקלד הערה ולחץ +"
          value={draft}
          maxLength={MAX_NOTE_LENGTH}
          {...inputProps}
          disabled={disabled}
          radius="md"
          className={classes.input}
          onChange={(event) => setDraft(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            // The field lives inside the category <form>; Enter must add a chip, not submit.
            event.preventDefault();
            add();
          }}
        />
        <ActionIcon
          aria-label={addLabel}
          title={addLabel}
          data-testid="category-quick-notes-add"
          variant="filled"
          color="blue"
          radius="md"
          size="lg"
          disabled={disabled || !canAdd}
          onClick={add}
        >
          <FontAwesomeIcon icon={faPlus} />
        </ActionIcon>
      </div>
      {value.length > 0 && (
        <div className={classes.chips} data-testid="category-quick-notes-chips">
          {value.map((note) => (
            <Pill
              key={note}
              title={note}
              withRemoveButton={!disabled}
              onRemove={() => onChange(value.filter((candidate) => candidate !== note))}
              data-testid={`category-quick-note-${note}`}
            >
              {note}
            </Pill>
          ))}
        </div>
      )}
    </div>
  );
};

export const QuickNotesField = React.memo(QuickNotesFieldComponent);
